import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Button } from "@fp/ui/components/base/buttons/button";
import { useUserContext } from "@/components/user-context";
import { useGetTermsOfService, useAcceptTermsOfService } from "@fp/admin-api-client/legal/legal.client";

export const Route = createFileRoute("/terms-acceptance")({
  component: RouteComponent,
});

function RouteComponent() {
  const navigate = useNavigate();
  const { refetchTermsStatus, logout } = useUserContext();
  const { data: termsResponse, isLoading, error: fetchError } = useGetTermsOfService();
  const acceptMutation = useAcceptTermsOfService();
  const content = termsResponse?.data ?? null;
  const [hasAgreed, setHasAgreed] = useState(false);

  const canAccept = hasAgreed && !acceptMutation.isPending;

  const handleAccept = () => {
    if (!content || !canAccept) return;
    acceptMutation.mutate(
      { data: { legalDocumentId: content.documentId } },
      {
        onSuccess: async () => {
          await refetchTermsStatus();
          navigate({ to: "/dashboard" });
        },
      },
    );
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 p-6">
      <div className="w-full max-w-2xl">
        <div className="mb-8 text-center">
          <img
            src="/Logo-green-bg.svg"
            alt="FieldPay"
            className="w-16 h-16 mx-auto mb-4"
          />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Terms & Conditions</h1>
          <p className="text-gray-600">
            Please review and accept the terms and conditions to continue.
          </p>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
          {isLoading ? (
            <div className="flex items-center justify-center p-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" />
            </div>
          ) : fetchError ? (
            <div className="p-6 text-center">
              <p className="text-red-600 mb-4">Failed to load terms and conditions. Please try again.</p>
              <Button color="secondary" onClick={() => window.location.reload()}>
                Retry
              </Button>
            </div>
          ) : content ? (
            <div className="p-6 max-h-[60vh] overflow-y-auto prose prose-sm prose-gray max-w-none">
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={{ a: ({ node, ...props }) => <a {...props} target="_blank" rel="noreferrer" /> }}>
                {content.content}
              </ReactMarkdown>
            </div>
          ) : null}
        </div>

        {content && (
          <div className="mt-6 mx-auto max-w-sm">
            {acceptMutation.error && <p className="text-sm text-red-600 mb-4 text-center">Failed to accept terms. Please try again.</p>}
            <label className="flex items-center gap-3 mb-4 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={hasAgreed}
                onChange={(e) => setHasAgreed(e.target.checked)}
                className="h-5 w-5 rounded border-gray-300 text-brand-600 focus:ring-brand-500 shrink-0"
              />
              <span className="text-sm text-gray-700">I have read and agree to the Terms of Service</span>
            </label>
            <Button
              color="primary"
              onClick={handleAccept}
              isLoading={acceptMutation.isPending}
              className={`w-full ${!canAccept ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              I Accept
            </Button>
            <button
              type="button"
              onClick={logout}
              className="mt-4 w-full text-sm text-gray-500 hover:text-gray-700"
            >
              Sign Out
            </button>
          </div>
        )}
      </div>
    </div>
  );
}